import React, { useState } from 'react';
// Context
import { useBooking } from '../../../context/BookingContext';
import client from '../../../api/client';
import { EDIT_OPENING_TIMES_API } from '../../../utils/ApiRoutes';
import LoadingSpinner from '../../utils/LoadingSpinner';

const daysOfWeek = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

function BookingOpeningTimes() {
  const { openingTimes, setOpeningTimes } = useBooking();
  const [editingDay, setEditingDay] = useState(null);
  const [showTimes, setShowTimes] = useState(false); // start closed
  const [isSaving, setIsSaving] = useState(false); // start closed
  const [formData, setFormData] = useState({
    dayOfWeek: 1,
    open: true,
    start: '',
    end: '',
  });

  const handleEditClick = (day) => {
    const dayData = openingTimes[day];
    setFormData({
      dayOfWeek: day,
      open: dayData.open,
      start: dayData.start || '',
      end: dayData.end || '',
    });
    setEditingDay(day);
  };

  const handleSave = async () => {
    setIsSaving(true);
    client
      .patch(EDIT_OPENING_TIMES_API, formData, true)
      .then((res) => {
        setOpeningTimes(res.data.updatedTimes);
        setEditingDay(null);
        setIsSaving(false);
      })
      .catch((err) => {
        console.error('Edit opening times failed', err);
        setEditingDay(null);
        setIsSaving(false);
      });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <section className='grid w-full'>
      {/* Toggle button */}
      <div className='grid px-8 lg:px-24 py-2 justify-end'>
        <button
          onClick={() => setShowTimes((prev) => !prev)}
          className='bg-colour5 text-colour1 px-4 py-2 rounded shadow hover:brightness-90 w-fit'
        >
          {showTimes ? 'Hide Opening Times' : 'Show Opening Times'}
        </button>
      </div>

      {/* Opening times list */}
      {showTimes && (
        <div className='grid h-fit gap-y-2 w-full lg:container lg:mx-auto px-8 py-2 rounded-xl lg:px-24 bg-colour5'>
          {Object.values(openingTimes).map((time) => (
            <div
              key={time.dayOfWeek}
              className='border bg-colour1 py-1 px-4 rounded flex justify-between items-center'
            >
              <div className='grid grid-flow-col gap-x-8'>
                <div className='font-semibold'>
                  {daysOfWeek[time.dayOfWeek]}
                </div>

                {editingDay === time.dayOfWeek ? (
                  <div className='mt-2 space-y-2'>
                    <div>
                      <label className='flex items-center'>
                        <span className='w-16'>Open:</span>
                        <input
                          type='checkbox'
                          name='open'
                          checked={formData.open}
                          onChange={handleChange}
                          className='ml-2 bg-colour4'
                        />
                      </label>
                    </div>
                    <div className='flex items-center'>
                      <span className='w-16'>Start:</span>
                      <input
                        type='time'
                        name='start'
                        value={formData.start}
                        onChange={handleChange}
                        className='ml-2 bg-colour4 border rounded px-2 py-1'
                        disabled={!formData.open}
                      />
                    </div>
                    <div className='flex items-center'>
                      <span className='w-16'>End:</span>
                      <input
                        type='time'
                        name='end'
                        value={formData.end}
                        onChange={handleChange}
                        className='ml-2 bg-colour4 border rounded px-2 py-1'
                        disabled={!formData.open}
                      />
                    </div>
                    <div className='flex space-x-2 mt-2'>
                      <button
                        onClick={handleSave}
                        className='bg-green-500 hover:bg-green-600 text-colour1 px-3 py-1 rounded'
                      >
                        {isSaving ? (<div><LoadingSpinner xxs={true} /></div>) : 'Save' }
                      </button>
                      <button
                        onClick={() => setEditingDay(null)}
                        className='bg-gray-300 hover:bg-gray-400 text-black px-3 py-1 rounded'
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className='text-colour2 bg-blue-200 mt-1 px-2'>
                    {time.open ? `${time.start} - ${time.end}` : 'Closed'}
                  </div>
                )}
              </div>

              {editingDay !== time.dayOfWeek && (
                <button
                  onClick={() => handleEditClick(time.dayOfWeek)}
                  className='text-blue-500 underline hover:text-blue-700'
                >
                  Edit
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default BookingOpeningTimes;
